import { Component, OnInit, effect, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SiFooterComponent } from '@siemens/element-ng/footer';
import { Link } from '@siemens/element-ng/link';
import { SiLoadingSpinnerComponent } from '@siemens/element-ng/loading-spinner';
import { TenantRoles } from '@sal/shared/domain/value-objects';
import { AuthService } from './auth/services/auth.service';

import { NgClass, ViewportScroller } from '@angular/common';
import { HostListener } from '@angular/core';
import { dispatch } from '@ngxs/store';
import { NavbarVerticalItem, SiNavbarVerticalComponent } from '@siemens/element-ng/navbar-vertical';
import { SiToastNotificationService } from '@siemens/element-ng/toast-notification';
import { TenantAuthorizationService } from './auth/services/tenant-authorization.service';
import { getFooterLinks } from './core/config/footer-links.config';
import { getNavTabs } from './core/config/navigation.config';
import { BottomNavigationComponent } from './shared/components/bottom-navigation/bottom-navigation.component';
import { HeaderComponent } from './shared/components/header/header.component';
import { SearchBoxComponent } from './shared/components/search-box/search-box.component';
import { EnergyBannerDialogService } from './shared/services/energy-banner-dialog.service';
import { PopupDialogService } from './shared/services/popup-dialog.service';
import { SearchService } from './shared/services/search.service';
import { TileService } from './shared/services/tile.service';
import { LoadCoreData } from './shared/state/pages/pages.actions';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [
        HeaderComponent,
        NgClass,
        SearchBoxComponent,
        SiNavbarVerticalComponent,
        RouterOutlet,
        BottomNavigationComponent,
        SiFooterComponent,
        TranslatePipe,
        SiLoadingSpinnerComponent
    ]
})
export class AppComponent implements OnInit {
    protected readonly authService = inject(AuthService);
    private readonly loadCoreDataAction = dispatch(LoadCoreData);
    private readonly router = inject(Router);
    private readonly translate = inject(TranslateService);
    private readonly searchService = inject(SearchService);
    private readonly popupDialogService = inject(PopupDialogService);
    private readonly energyBannerDialogService = inject(EnergyBannerDialogService);
    private readonly route = inject(ActivatedRoute);
    private readonly _tileSrv = inject(TileService);
    private readonly scroller = inject(ViewportScroller);
    private readonly toastNotificationService = inject(SiToastNotificationService);
    protected readonly tenantAuthorizationService = inject(TenantAuthorizationService);

    previousPath = '';
    fileDownloaded = false;
    isBottomVisible = false;
    isBottomNavExpanded = false;
    protected tabs: NavbarVerticalItem[] = [];
    protected footerLinks: Link[] = [];
    protected readonly footerCopyright = 'Siemens AG ' + new Date().getFullYear();

    protected isRouterPathUnauthorized(): boolean {
        return this.router.url === '/unauthorized' || this.router.url === '/403';
    }

    protected isEnergyTenant(): boolean {
        return (
            this.tenantAuthorizationService.activeTenantRole() === TenantRoles.SE ||
            this.tenantAuthorizationService.getRequiredTenantRole() === TenantRoles.SE
        );
    }

    protected refreshNavigationAndFooterLinks(): void {
        this.tabs = getNavTabs(this.translate, this.authService.isAdmin());
        this.footerLinks = getFooterLinks(this.translate);
    }

    constructor() {
        this.translate.onLangChange.subscribe(() => this.refreshNavigationAndFooterLinks());
        effect(() => this.refreshNavigationAndFooterLinks());

        this.translate.setFallbackLang('en');
        const lang = localStorage.getItem('SAL_default_language');
        this.translate.use(lang ?? 'en');
        this.route.queryParams.subscribe(params => {
            if (params.dl && !this.fileDownloaded) {
                const url = decodeURIComponent(params.dl);
                const key = url.split('docs.sal.siemens.cloud/')[1];

                if (key) {
                    this._tileSrv.readDocument(key).then(signedfileUrl => {
                        if (typeof signedfileUrl !== 'string') {
                            return;
                        }

                        this.triggerBrowserDownload(signedfileUrl);
                        this.toastNotificationService.showToastNotification({
                            state: 'success',
                            title: 'Download',
                            message: 'File downloaded',
                            timeout: 2000
                        });
                        this.fileDownloaded = true;
                    });
                }
            }
        });

        effect(() => {
            if (!this.authService.isAuthLoading() && this.authService.isAuthenticated()) {
                this.loadCoreData();
                if (this.isEnergyTenant()) {
                    this.energyBannerDialogService.openDialog();
                }
            }
        });
    }

    async ngOnInit() {
        this.routeToRedirect();
        if (this.isEnergyTenant()) {
            this.energyBannerDialogService.openDialog();
        }
        this.searchService.hasData().then(hasData => {
            if (!hasData) {
                // there are no data in the index, so it is assumed to be updated
                this.popupDialogService.open();
            }
        });
    }
    // it is button which scroll window to the footer side
    scrollToBottom() {
        document.body.style.overflow = 'auto';
        this.scroller.scrollToAnchor('lay-wrapper-bottom-nav-section');
    }
    @HostListener('window:scroll', []) onWindowScroll(): void {
        const verticalOffset = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
        const viewportBottom = verticalOffset + window.innerHeight;
        const bottomNavSection = document.querySelector('#lay-wrapper-bottom-nav-section');
        const bottomNavTop = bottomNavSection
            ? bottomNavSection.getBoundingClientRect().top + verticalOffset
            : Number.POSITIVE_INFINITY;

        // Keep the contact tab fixed at viewport bottom until the bottom navigation section becomes visible.
        this.isBottomVisible = viewportBottom >= bottomNavTop;
    }
    loadCoreData(): void {
        void this.loadCoreDataAction();
    }

    getRouteAnimation(outlet: { activatedRouteData: { animation: any } }) {
        return outlet.activatedRouteData.animation;
    }

    private routeToRedirect(): void {
        const url = window.localStorage.getItem('redirectUrl');
        if (url) {
            window.localStorage.removeItem('redirectUrl');
            if (!this.isEnergyTenant()) {
                void this.router.navigateByUrl(url);
            }
        }
    }

    private triggerBrowserDownload(url: string): void {
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
}
